import React from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../utils/dateLocale';

const DOC_TIPO_LABELS = {
  80: 'CUIT',
  86: 'CUIL',
  96: 'DNI',
  99: 'Sin identificar',
};

const InvoiceDetailModal = ({ invoice, onClose, onCreditNote, onDownloadPdf }) => {
  const { t, i18n } = useTranslation();

  const formatComprobante = () => {
    const pv = String(invoice.puntoDeVenta || 0).padStart(5, '0');
    const num = String(invoice.numeroComprobante || 0).padStart(8, '0');
    return `${pv}-${num}`;
  };

  const getTypeLabel = () => {
    const labels = {
      invoice: 'Factura',
      credit_note: 'Nota de Crédito',
      debit_note: 'Nota de Débito',
    };
    return `${labels[invoice.documentType] || 'Comprobante'} ${invoice.invoiceClass || ''}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {getTypeLabel()} {formatComprobante()}
          </h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="modal-body">
          <div className="invoice-detail-grid">
            {/* Left: Invoice info */}
            <div className="invoice-detail-section">
              <h3>{t('invoicing.invoiceData', 'Datos del Comprobante')}</h3>
              <div className="invoice-detail-rows">
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.type', 'Tipo')}:</span>
                  <span>{getTypeLabel()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.number', 'Número')}:</span>
                  <span className="mono">{formatComprobante()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.date', 'Fecha')}:</span>
                  <span>
                    {new Date(invoice.createdAt).toLocaleString(getDateLocale(i18n.language))}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">CAE:</span>
                  <span className="mono">{invoice.cae || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.caeExpiry', 'Vto. CAE')}:</span>
                  <span>{invoice.caeVencimiento || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.status', 'Estado')}:</span>
                  <span className={`invoicing-status ${invoice.status}`}>
                    {invoice.status === 'authorized' ? 'Autorizada' : invoice.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Receptor */}
            <div className="invoice-detail-section">
              <h3>{t('invoicing.receptorData', 'Datos del Receptor')}</h3>
              <div className="invoice-detail-rows">
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.receptorName', 'Nombre')}:</span>
                  <span>{invoice.receptorNombre || 'Consumidor Final'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.document', 'Documento')}:</span>
                  <span>
                    {DOC_TIPO_LABELS[invoice.receptorDocTipo] || ''} {invoice.receptorDocNro || '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('invoicing.condicionIVA', 'Cond. IVA')}:</span>
                  <span>{t(`fiscal.ivaCondicion_${invoice.receptorCondicionIVA}`, '-')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Amounts */}
          <div className="invoice-detail-amounts">
            <h3>{t('invoicing.amounts', 'Importes')}</h3>
            <div className="invoice-detail-rows">
              {invoice.invoiceClass === 'A' && (
                <>
                  <div className="detail-row">
                    <span className="detail-label">
                      {t('invoicing.netAmount', 'Importe Neto')}:
                    </span>
                    <span className="mono">${(invoice.importeNeto || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">IVA 21%:</span>
                    <span className="mono">${(invoice.importeIVA || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="detail-row detail-row--total">
                <span className="detail-label">{t('invoicing.total', 'TOTAL')}:</span>
                <span className="mono">${(invoice.importeTotal || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Related invoice for NC/ND */}
          {invoice.relatedInvoiceId && (
            <div className="invoice-detail-related">
              <span className="material-icons">link</span>
              {t('invoicing.relatedTo', 'Relacionada a')}: {invoice.relatedInvoiceId}
              {invoice.reason && ` — ${invoice.reason}`}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.close', 'Cerrar')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onDownloadPdf(invoice.invoiceId)}
          >
            <span className="material-icons">picture_as_pdf</span>
            {t('invoicing.downloadPdf', 'Descargar PDF')}
          </button>
          {invoice.documentType === 'invoice' && invoice.status === 'authorized' && (
            <button type="button" className="btn btn-danger" onClick={() => onCreditNote(invoice)}>
              <span className="material-icons">undo</span>
              {t('invoicing.createCreditNote', 'Nota de Crédito')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailModal;
